import { useEffect } from 'react';
import { useQuery } from 'react-query';
import { useAuthStore } from '@/store/useAuthStore';
import { apiClient } from '@/api/client';
import PhoneInterface from '@/components/PhoneInterface';
import LoadingSpinner from '@/components/LoadingSpinner';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/utils/constants';

const VoiceAssistantPage = () => {
  const { userProfile } = useAuthStore();
  const navigate = useNavigate();

  // Check if user has Business or Enterprise plan
  useEffect(() => {
    const plan = userProfile?.plan;
    if (plan && !['business', 'enterprise'].includes(plan)) {
      toast.error('Ajebota Voice Assistant is only available for Business and Enterprise plans');
      navigate(ROUTES.PRICING);
    }
  }, [userProfile, navigate]);

  // Check Vapi.ai service status
  const { data: vapiStatus, isLoading: isLoadingStatus } = useQuery(
    'vapi-status',
    () => apiClient.getVapiStatus(),
    {
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );

  if (isLoadingStatus) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '60vh' }}>
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!vapiStatus?.configured) {
    return (
      <div>
        <div className="title-one mb-40">
          <h2>Ajebota Voice Assistant</h2>
          <p className="text-dark">AI-powered voice assistant for finding businesses</p>
        </div>
        <div className="card-style-one border-30 bg-white p-5 text-center">
          <div className="mb-4" style={{ fontSize: '60px' }}>🔧</div>
          <h4>Service Unavailable</h4>
          <p className="text-dark">
            The Ajebota Voice Assistant service is currently being configured. Please check back later.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="title-one mb-40">
        <h2>Ajebota Voice Assistant</h2>
        <p className="text-dark">
          Find businesses in Jos, Lagos, Abuja, and Port Harcourt using voice commands
        </p>
      </div>

      {/* Info Cards */}
      <div className="row gx-xl-4 mb-50">
        <div className="col-md-4 mt-30">
          <div className="card-style-one border-30 bg-white p-4 h-100">
            <div className="mb-3" style={{ fontSize: '40px' }}>🏙️</div>
            <h5 className="fw-bold mb-2">Multiple Cities</h5>
            <p className="text-dark mb-0" style={{ fontSize: '14px' }}>
              Search businesses in Jos, Lagos, Abuja, and Port Harcourt
            </p>
          </div>
        </div>
        <div className="col-md-4 mt-30">
          <div className="card-style-one border-30 bg-white p-4 h-100">
            <div className="mb-3" style={{ fontSize: '40px' }}>🍕</div>
            <h5 className="fw-bold mb-2">All Categories</h5>
            <p className="text-dark mb-0" style={{ fontSize: '14px' }}>
              Restaurants, pastries, churches, mosques, and more
            </p>
          </div>
        </div>
        <div className="col-md-4 mt-30">
          <div className="card-style-one border-30 bg-white p-4 h-100">
            <div className="mb-3" style={{ fontSize: '40px' }}>📞</div>
            <h5 className="fw-bold mb-2">Get Contacts</h5>
            <p className="text-dark mb-0" style={{ fontSize: '14px' }}>
              Receive business contact information directly
            </p>
          </div>
        </div>
      </div>

      {/* Phone Interface */}
      <div className="card-style-one border-30 bg-white p-5">
        <div className="text-center mb-4">
          <h4 className="mb-2">Start a Conversation</h4>
          <p className="text-dark" style={{ fontSize: '14px' }}>
            Click the call button to start finding businesses with your voice
          </p>
        </div>
        <PhoneInterface 
          onCallEnd={() => {
            // Optional: Handle call end
            console.log('Call ended');
          }}
        />
      </div>

      {/* Instructions */}
      <div className="card-style-one border-30 bg-white p-4 mt-30">
        <h5 className="mb-3">How to Use</h5>
        <ol className="text-dark" style={{ paddingLeft: '20px' }}>
          <li className="mb-2">Click the call button to start a conversation</li>
          <li className="mb-2">Speak naturally - ask about businesses in any of the supported cities</li>
          <li className="mb-2">The assistant will help you find restaurants, pastries, churches, mosques, and more</li>
          <li className="mb-2">You can request business contact information</li>
          <li className="mb-2">Click the call button again to end the conversation</li>
        </ol>
      </div>
    </div>
  );
};

export default VoiceAssistantPage;

